import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, formatError, fmtDate } from "@/lib/api";

const STATUSES = ["new", "contacted", "won", "closed"];
const LABELS = { bulk: "Bulk", white_label: "White-label", api: "API", sso: "Teams/SSO", support: "SLA" };

const Row = ({ e, onSaved }) => {
  const [note, setNote] = useState(e.admin_note || "");
  const [busy, setBusy] = useState(false);
  const save = async (status) => {
    setBusy(true);
    try {
      onSaved((await api.patch(`/admin/enquiries/${e.id}`, { status, admin_note: note })).data);
      toast.success("Enquiry updated");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  const k = e.id.slice(0, 8);
  return (
    <div data-testid={`admin-enquiry-${k}`} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 space-y-3">
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <p className="font-display font-semibold">{e.org_name} <span className="text-xs font-normal text-slate-500">· {e.org_type}</span></p>
          <p className="text-sm text-slate-600 dark:text-slate-300">{e.contact_name} · <a href={`mailto:${e.email}`} className="underline">{e.email}</a>{e.phone && ` · ${e.phone}`}</p>
        </div>
        <div className="text-right text-xs text-slate-500"><p className="font-mono">{fmtDate(e.created_at)}</p><p className="mt-1">Volume: <span className="font-semibold text-slate-900 dark:text-white">{e.monthly_volume}</span></p></div>
      </div>
      {!!e.interests?.length && <div className="flex flex-wrap gap-1.5">{e.interests.map((i) => <span key={i} className="px-2 py-0.5 rounded-full text-[11px] bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">{LABELS[i] || i}</span>)}</div>}
      {e.message && <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{e.message}</p>}
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
        <input data-testid={`admin-enquiry-note-${k}`} value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="Internal note" className="flex-1 min-w-[180px] h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm" />
        {STATUSES.map((s) => (
          <button key={s} data-testid={`admin-enquiry-status-${s}-${k}`} disabled={busy} onClick={() => save(s)}
            className={`px-3 h-9 rounded-full text-xs font-semibold capitalize border transition-colors ${e.status === s ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900" : "border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>{s}</button>
        ))}
      </div>
    </div>
  );
};

export const AdminEnquiries = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/enquiries").then((r) => setRows(r.data)); }, []);
  if (!rows) return <Loader2 className="animate-spin text-emerald-600" />;
  const saved = (u) => setRows(rows.map((r) => (r.id === u.id ? u : r)));
  return (
    <div className="space-y-4" data-testid="admin-enquiries">
      <p className="text-sm text-slate-500">Enterprise and white-label enquiries from <a href="/enterprise" className="underline">/enterprise</a>. Each one is also emailed to bookings@postalcloud.app.</p>
      {!rows.length && <p className="text-sm text-slate-500 p-6 text-center rounded-xl border border-slate-200 dark:border-slate-800">No enquiries yet.</p>}
      {rows.map((e) => <Row key={e.id} e={e} onSaved={saved} />)}
    </div>
  );
};
